/**
 * Dashboard Widget Service
 * The signed-in user's Dashboard (placements, Division filter, widget data) and Widget Subscriptions.
 */
import BaseApiService from '../base/BaseApiService';
import type {
  MyDashboardData,
  WidgetData,
  WidgetAccessMatrixData,
  WidgetSubscriptionSchedule,
  WidgetSubscriptionsData
} from '@app-types';

const MY_DASHBOARD = '/dashboard-widgets/me';
const WIDGET_ACCESS = '/dashboard-widgets/access';

class DashboardWidgetService extends BaseApiService {
  /** The widgets available to the user's roles, their placements in order and the saved Division filter. */
  async getMyDashboard(): Promise<MyDashboardData> {
    return this.get<MyDashboardData>(MY_DASHBOARD);
  }

  /** Add a widget at the end of the Dashboard. Fails with 403 when the user's roles have no Widget Access to it. */
  async addWidget(widgetKey: string): Promise<void> {
    await this.put(`${MY_DASHBOARD}/widgets/${encodeURIComponent(widgetKey)}`);
  }

  /** Remove a widget from the Dashboard; this also ends the user's Widget Subscription to it. */
  async removeWidget(widgetKey: string): Promise<void> {
    await this.delete(`${MY_DASHBOARD}/widgets/${encodeURIComponent(widgetKey)}`);
  }

  /** Save the order of the widgets on the Dashboard: each widget key exactly once. */
  async reorderWidgets(widgetKeys: string[]): Promise<void> {
    await this.put(`${MY_DASHBOARD}/widget-order`, { widget_keys: widgetKeys });
  }

  /** Save the Division filter with the Dashboard. An empty list means All divisions. */
  async setDivisionFilter(divisionIds: number[]): Promise<void> {
    await this.put(`${MY_DASHBOARD}/division-filter`, { division_ids: divisionIds });
  }

  /**
   * A widget's data, narrowed to the saved Division filter. 404 while the widget has no data source yet.
   * @param dimension - One of the widget's `dimensions`; omitted, the widget uses its first one
   * @param period - One of the widget's `periods`; omitted, the widget uses its first one
   */
  async getWidgetData(widgetKey: string, dimension?: string, period?: string): Promise<WidgetData> {
    const params = { ...(dimension ? { dimension } : {}), ...(period ? { period } : {}) };
    return this.get<WidgetData>(
      `${MY_DASHBOARD}/widgets/${encodeURIComponent(widgetKey)}/data`,
      Object.keys(params).length > 0 ? params : undefined
    );
  }

  /** The user's Widget Subscriptions; one is paused while the user's roles have no Widget Access to its widget. */
  async getSubscriptions(): Promise<WidgetSubscriptionsData> {
    return this.get<WidgetSubscriptionsData>(`${MY_DASHBOARD}/subscriptions`);
  }

  /** Subscribe to a widget on the Dashboard, or change the schedule. The time and day are fixed by the server. */
  async subscribe(widgetKey: string, schedule: WidgetSubscriptionSchedule): Promise<void> {
    await this.put(`${MY_DASHBOARD}/widgets/${encodeURIComponent(widgetKey)}/subscription`, { schedule });
  }

  /** Turn the Widget Subscription off. */
  async unsubscribe(widgetKey: string): Promise<void> {
    await this.delete(`${MY_DASHBOARD}/widgets/${encodeURIComponent(widgetKey)}/subscription`);
  }

  /** Admin: the catalog's widgets by every role of the system, with the granted widget/role pairs. */
  async getAccessMatrix(): Promise<WidgetAccessMatrixData> {
    return this.get<WidgetAccessMatrixData>(WIDGET_ACCESS);
  }

  /** Admin: give a role access to a widget. Idempotent. */
  async grantAccess(widgetKey: string, roleId: number): Promise<void> {
    await this.put(`${WIDGET_ACCESS}/${encodeURIComponent(widgetKey)}/roles/${roleId}`);
  }

  /**
   * Admin: remove a role's access to a widget. Users who no longer have access through any of their roles lose the
   * widget on their Dashboard and get no data for it, and their subscriptions to it are paused.
   */
  async revokeAccess(widgetKey: string, roleId: number): Promise<void> {
    await this.delete(`${WIDGET_ACCESS}/${encodeURIComponent(widgetKey)}/roles/${roleId}`);
  }
}

export const dashboardWidgetService = new DashboardWidgetService();
